// Plaid API client
// Requests go through the Cloud Function proxy at /api/plaid
// which handles Plaid SDK calls and Firestore storage

const PLAID_API_BASE = '/api/plaid';

/**
 * Core POST fetch with error handling.
 * @param {string} action - Plaid action (create_link_token, exchange_public_token, etc.)
 * @param {Object} data - Action-specific data
 * @returns {Promise<Object>} Parsed JSON response
 */
export const plaidFetch = async (action, data = {}) => {
  const url = `${window.location.origin}${PLAID_API_BASE}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, data }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Plaid API ${response.status}: ${text || response.statusText}`);
  }

  return response.json();
};

/**
 * Create a Plaid Link token for the Plaid Link widget.
 * @param {string} userId - Current user ID
 * @returns {Promise<Object>} { link_token }
 */
export const createLinkToken = (userId) =>
  plaidFetch('create_link_token', { userId });

/**
 * Exchange a public token from Plaid Link for a stored access token.
 * Access token is stored server-side in Firestore, never returned.
 * @param {string} publicToken - Public token from Plaid Link onSuccess
 * @returns {Promise<Object>} { item_id, success }
 */
export const exchangePublicToken = (publicToken) =>
  plaidFetch('exchange_public_token', { public_token: publicToken });

/**
 * Sync transactions for a connected bank account.
 * @param {string} itemId - Plaid item ID
 * @returns {Promise<Object>} { success, added, modified, removed, written }
 */
export const syncTransactions = (itemId) =>
  plaidFetch('sync_transactions', { item_id: itemId });

/**
 * Get accounts for a connected bank item.
 * @param {string} itemId - Plaid item ID
 * @returns {Promise<Object>} { accounts }
 */
export const getLinkedAccounts = (itemId) =>
  plaidFetch('get_accounts', { item_id: itemId });
